import React, { useMemo, useState } from "react"
import { InfoCircleOutlined } from "@ant-design/icons"
import { translations } from "../../../utils/Translation/staticTranslation"
import { getMetadataCategoryLabel } from "../../../utils/plansPricingPayload"
import { PLAN_METADATA_SECTIONS } from "../planMetadataSections"
import "./CreatePlan.css"

const ChevronIcon = ({ open, className = "" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} ${open ? "is-open" : ""}`}
    aria-hidden
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** User-facing preview only — admin plan category (standard/organization) is excluded. */
const CreatePlanPreview = ({ form }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.plansPricingPage?.createPlan
  const isArabic = lang === "ar"

  const [expandedKeys, setExpandedKeys] = useState(["feature_access"])

  const planName = isArabic
    ? form.planNameArabic?.trim() ||
      form.planName.trim() ||
      t?.preview_plan_name ||
      "Plan Name"
    : form.planName.trim() || t?.preview_plan_name || "Plan Name"

  const description = isArabic
    ? form.descriptionArabic?.trim() ||
      form.description.trim() ||
      t?.preview_plan_desc_placeholder ||
      "Plan description"
    : form.description.trim() ||
      t?.preview_plan_desc_placeholder ||
      "Plan description"

  const hasDescription = isArabic
    ? Boolean(form.descriptionArabic?.trim() || form.description?.trim())
    : Boolean(form.description?.trim())

  const images = form.images || "0"
  const price = form.price || "0"
  const isSubscription = form.planType === "Subscription"
  const isOneTime = form.planType === "One-Time"

  const billingSuffix = isSubscription
    ? form.billingCycle === "Yearly"
      ? t?.billing_yearly || "/year"
      : t?.billing_monthly || "/month"
    : ""

  const getFeaturePreviewText = (feature) => {
    if (isArabic) return (feature.labelAr || "").trim()
    return (feature.label || "").trim()
  }

  const featuresByCategory = useMemo(() => {
    const map = {}
    PLAN_METADATA_SECTIONS.forEach(({ key }) => {
      map[key] = form.features.filter(
        (f) => f.category === key && getFeaturePreviewText(f)
      )
    })
    return map
  }, [form.features, isArabic])

  const visibleSections = useMemo(
    () =>
      PLAN_METADATA_SECTIONS.filter(
        (section) => (featuresByCategory[section.key] || []).length > 0
      ),
    [featuresByCategory]
  )

  const toggleSection = (key) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  return (
    <div className="create-plan-preview-column">
      <div className="create-plan-preview-card">
        <div className="create-plan-preview-header">
          <h3 className="create-plan-preview-title">
            {t?.live_preview || "Live Preview"}
          </h3>
          <span className="create-plan-preview-subtitle">
            {t?.user_facing_view || "User facing view"}
          </span>
        </div>

        <div className="create-plan-preview-plan-card">
          <div className="create-plan-preview-plan-name">{planName}</div>
          <div className="create-plan-preview-meta">
            {images} {t?.preview_images || "Images"}
          </div>
          <div className="create-plan-preview-price">
            SAR {price}
            {isOneTime ? (
              <span className="create-plan-preview-price-note">
                ({t?.preview_no_subscriptions || "No monthly subscriptions"})
              </span>
            ) : (
              billingSuffix && (
                <span className="create-plan-preview-price-period">
                  {billingSuffix}
                </span>
              )
            )}
          </div>
          <p
            className={`create-plan-preview-desc ${
              !hasDescription ? "create-plan-preview-desc--empty" : ""
            }`}
          >
            {description}
          </p>

          {visibleSections.length > 0 && (
            <div className="create-plan-preview-accordions">
              {visibleSections.map((section, index) => {
                const Icon = section.Icon
                const isOpen = expandedKeys.includes(section.key)
                const items = featuresByCategory[section.key] || []
                const label =
                  getMetadataCategoryLabel(section.key, lang) ||
                  t?.[section.labelKey] ||
                  section.key

                return (
                  <div
                    key={section.key}
                    className={`create-plan-preview-accordion-panel ${
                      isOpen ? "is-open" : ""
                    } ${index === visibleSections.length - 1 ? "is-last" : ""}`}
                  >
                    <button
                      type="button"
                      className="create-plan-preview-accordion-header"
                      onClick={() => toggleSection(section.key)}
                      aria-expanded={isOpen}
                    >
                      <Icon className="create-plan-preview-accordion-icon" />
                      <span className="create-plan-preview-accordion-title">
                        {label}
                      </span>
                      <ChevronIcon
                        open={isOpen}
                        className="create-plan-preview-chevron"
                      />
                    </button>

                    {isOpen && (
                      <ul className="create-plan-preview-feature-list">
                        {items.map((feature) => (
                          <li
                            key={feature.id}
                            className="create-plan-preview-feature create-plan-preview-feature--included"
                          >
                            <span
                              className="create-plan-preview-marker create-plan-preview-marker--bullet"
                              aria-hidden
                            />
                            <span>{getFeaturePreviewText(feature)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="create-plan-preview-notes">
          <h4 className="create-plan-preview-notes-title">
            {t?.preview_notes_title || "Preview Notes"}
          </h4>
          <ul className="create-plan-preview-notes-list">
            <li>
              <InfoCircleOutlined className="create-plan-preview-notes-icon" />
              <span>
                {t?.preview_notes_line1 ||
                  "This is how users will see your plan"}
              </span>
            </li>
            <li>
              <InfoCircleOutlined className="create-plan-preview-notes-icon" />
              <span>
                {t?.preview_notes_line2 || "Changes update in real-time"}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default CreatePlanPreview
